import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { apiClient } from '../api'

const GATE_KEY = 'sun-ge-login-required-v1'
type PlayerAccessValue = {
  nickname: string
  avatarUrl: string
  busy: boolean
  error: string
  requestRevive: () => boolean
  submitName: (value: string) => Promise<boolean>
  submitAvatar: (image: string) => Promise<boolean>
}
export const PlayerAccessContext = createContext<PlayerAccessValue>({ nickname: '', avatarUrl: '', busy: false, error: '', requestRevive: () => true, submitName: async () => false, submitAvatar: async () => false })
export const usePlayerAccess = () => useContext(PlayerAccessContext)
function read(key: string) { try { return localStorage.getItem(key) || '' } catch { return '' } }
function write(key: string, value: string) { try { localStorage.setItem(key, value) } catch { /* Memory remains authoritative in this tab. */ } }

export function usePlayerIdentity(userId: string | null, authResolved: boolean) {
  const [gate, setGate] = useState(() => read(GATE_KEY))
  const [profile, setProfile] = useState<{ owner: string; nickname: string | null; avatarUrl: string | null } | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const owner = useRef(userId)
  owner.current = userId
  useEffect(() => {
    const sync = () => setGate(read(GATE_KEY))
    window.addEventListener('storage', sync)
    return () => window.removeEventListener('storage', sync)
  }, [])
  useEffect(() => {
    let stale = false
    setError('')
    if (!userId) { setProfile(null); return }
    setBusy(true)
    void apiClient.getPlayerProfile().then(value => {
      if (!stale) setProfile({ owner: userId, nickname: value.nickname, avatarUrl: value.avatarUrl })
    }).catch(reason => { if (!stale) setError(reason instanceof Error ? reason.message : '读取昵称失败，请重试') })
      .finally(() => { if (!stale) setBusy(false) })
    return () => { stale = true }
  }, [userId, reload])
  const nickname = profile?.owner === userId ? profile.nickname || '' : ''
  const avatarUrl = profile?.owner === userId ? profile.avatarUrl || '' : ''
  const requireLogin = (reason: 'revive' | 'rebirth' | 'vip') => { if (userId) return true; write(GATE_KEY, reason); setGate(reason); return false }
  const requestRevive = () => requireLogin('revive')
  const submitName = async (value: string) => {
    if (!userId || busy) return false
    const requestOwner = userId
    setBusy(true); setError('')
    try {
      const result = await apiClient.setPlayerProfile(value)
      if (owner.current === requestOwner) {
        setProfile({ owner: requestOwner, nickname: result.nickname, avatarUrl: result.avatarUrl })
        return true
      }
      return false
    } catch (reason) {
      if (owner.current === requestOwner) setError(reason instanceof Error ? reason.message : '保存失败，请重试')
      return false
    }
    finally { if (owner.current === requestOwner) setBusy(false) }
  }
  const submitAvatar = async (image: string) => {
    if (!userId || busy) return false
    const requestOwner = userId
    setBusy(true); setError('')
    try {
      const result = await apiClient.setPlayerAvatar(image)
      if (owner.current === requestOwner) {
        setProfile({ owner: requestOwner, nickname: result.nickname, avatarUrl: result.avatarUrl })
        return true
      }
      return false
    } catch (reason) {
      if (owner.current === requestOwner) setError(reason instanceof Error ? reason.message : '头像上传失败，请重试')
      return false
    }
    finally { if (owner.current === requestOwner) setBusy(false) }
  }
  const finishGate = () => { write(GATE_KEY, ''); setGate('') }
  const blocked = authResolved && (userId ? !nickname : Boolean(gate))
  return { nickname, avatarUrl, gate, blocked, busy, error, finishGate, requireLogin, requestRevive, submitName, submitAvatar, retry: () => setReload(x => x + 1) }
}

export function PlayerAccessDialog({ loggedIn, gate, busy, error, authError, onLogin, onSubmit, onRetry }: {
  loggedIn: boolean; gate: string; busy: boolean; error: string; authError: string
  onLogin: () => void; onSubmit: (name: string) => Promise<boolean>; onRetry: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [name, setName] = useState('')
  const [validation, setValidation] = useState('')
  useEffect(() => { const element = dialog.current; element?.showModal(); return () => element?.close() }, [])
  return <dialog ref={dialog} className="player-access-dialog" aria-labelledby="player-access-title" onCancel={event => event.preventDefault()}>
    <h2 id="player-access-title">{loggedIn ? '给这一世取个名字' : '登录后继续人生'}</h2>
    {loggedIn ? <form onSubmit={event => { event.preventDefault(); const value = name.normalize('NFKC').trim(); if (!/^[\p{L}\p{N}· _-]{1,12}$/u.test(value)) { setValidation('请输入 1–12 个字，可用中英文、数字、空格、下划线或短横线'); return } setValidation(''); void onSubmit(value) }}>
      <p>这个名字会出现在你的结局、VIP 称呼和页面互动中。</p>
      <label htmlFor="player-nickname">你的名字</label>
      <input id="player-nickname" value={name} onChange={event => setName(event.target.value)} maxLength={24} autoComplete="nickname" placeholder="例如：小太阳" disabled={busy} />
      {(validation || error) && <p role="alert">{validation || error}</p>}
      <button className="primary-button full" type="submit" disabled={busy}>{busy ? '正在读取或保存…' : '就叫这个，继续人生'}</button>
      {error && <><button className="text-button" type="button" onClick={onRetry} disabled={busy}>重新读取账号资料</button><button className="text-button" type="button" onClick={onLogin}>重新登录</button></>}
    </form> : <>
      <p>{gate === 'vip' ? '开通 VIP 需要绑定账号，权益会跟随你的账号保存。' : gate === 'rebirth' ? '重新开始一段人生必须先登录。登录完成后即可触发重生。' : '复活会继续当前人生进度，必须先登录。登录完成后即可继续。'}</p>
      {authError && <p role="alert">{authError}</p>}
      <button className="primary-button full" onClick={onLogin}>手机号登录</button>
    </>}
  </dialog>
}
